/**
 * Converts a File or Blob to a WebP Blob.
 *
 * @param file The original image file.
 * @param quality Quality from 0 to 1. Default is 0.8.
 * @returns A promise that resolves to a new File object in WebP format.
 */
export const convertToWebP = async (
  file: File,
  quality = 0.8
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Could not get canvas context"));
          return;
        }
        ctx.drawImage(img, 0, 0);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const lastDotIndex = file.name.lastIndexOf(".");
              const baseName =
                lastDotIndex !== -1
                  ? file.name.substring(0, lastDotIndex)
                  : file.name;
              const newFileName = `${baseName}.webp`;
              const webpFile = new File([blob], newFileName, {
                type: "image/webp",
                lastModified: Date.now(),
              });
              resolve(webpFile);
            } else {
              reject(new Error("Canvas toBlob failed"));
            }
          },
          "image/webp",
          quality
        );
      };
      img.onerror = () => reject(new Error("Image loading failed"));
    };
    reader.onerror = () => reject(new Error("FileReader failed"));
  });
};
